import {
  Home,
  Newspaper,
  Users,
  Calendar,
  LayoutGrid,
  BookOpen,
  MessageSquare,
  Building2,
  Award,
  FileText,
} from 'lucide-react'

export interface NavItem {
  label: string
  icon: any
  to: string
  badge?: number
  end?: boolean
}

export const navItems: NavItem[] = [
  { label: 'Home', icon: Home, to: '/', end: true },
  { label: 'News', icon: Newspaper, to: '/news', badge: 3 },
  { label: 'Feed', icon: MessageSquare, to: '/feed' },
  { label: 'People', icon: Users, to: '/people' },
  { label: 'Teams & Sites', icon: Building2, to: '/teams' },
  { label: 'Events', icon: Calendar, to: '/events', badge: 2 },
  { label: 'Apps', icon: LayoutGrid, to: '/apps' },
  { label: 'Knowledge', icon: BookOpen, to: '/knowledge' },
  { label: 'Recognition', icon: Award, to: '/recognition' },
  { label: 'Documents', icon: FileText, to: '/documents' },
]

/* ----------------------------- Featured / Home ---------------------------- */

export interface FeaturedStory {
  id: string
  category: string
  title: string
  excerpt: string
  author: string
  date: string
  image: string
  accent: string
}

export const featuredStories: FeaturedStory[] = [
  {
    id: 'a1',
    category: 'Company News',
    title: 'We funded $2.1B in home loans this quarter — a new record',
    excerpt:
      'Thanks to every team member, Independence Home Loans helped more families reach the closing table than ever before. Here is what drove the milestone.',
    author: 'Dana Whitfield, CEO',
    date: 'Today',
    image:
      'https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?q=80&w=2070&auto=format&fit=crop',
    accent: 'navy',
  },
  {
    id: 'a2',
    category: 'Rate Desk',
    title: 'Q3 rate-lock policy update: what every loan officer needs to know',
    excerpt:
      'Effective Monday, lock extensions move to a 3-tier structure. Review the updated matrix before submitting new locks.',
    author: 'Secondary Marketing',
    date: 'Yesterday',
    image:
      'https://images.unsplash.com/photo-1554224155-6726b3ff858f?q=80&w=2011&auto=format&fit=crop',
    accent: 'gold',
  },
  {
    id: 'a3',
    category: 'Growth',
    title: 'Independence opens a new branch in Austin, TX',
    excerpt:
      'Our 14th location is now welcoming borrowers in Central Texas. Meet the team leading the expansion.',
    author: 'People & Culture',
    date: 'Mon',
    image:
      'https://images.unsplash.com/photo-1531218150217-54595bc2b934?q=80&w=2070&auto=format&fit=crop',
    accent: 'sage',
  },
]

/* --------------------------------- News ---------------------------------- */

export interface Article {
  id: string
  category: string
  title: string
  excerpt: string
  author: string
  authorAvatar: string
  date: string
  readTime: string
  image: string
  body: string[]
  needsReview?: boolean
}

export const articles: Article[] = [
  {
    id: 'a1',
    category: 'Company News',
    title: 'We funded $2.1B in home loans this quarter — a new record',
    excerpt:
      'Independence Home Loans helped more families reach the closing table than ever before. Here is what drove the milestone.',
    author: 'Dana Whitfield',
    authorAvatar: 'https://i.pravatar.cc/120?img=32',
    date: 'June 30, 2026',
    readTime: '4 min read',
    image:
      'https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?q=80&w=2070&auto=format&fit=crop',
    body: [
      'This quarter, the Independence team achieved something extraordinary: we funded over $2.1 billion in home loans, the highest single-quarter volume in company history.',
      'Behind that number are thousands of families who now have keys to a home of their own — and a team that refused to let a single file fall through the cracks.',
      'Our branch network grew to 14 locations, our average time-to-close dropped to 21 days, and borrower satisfaction reached an all-time high of 4.9 out of 5.',
      'To every loan officer, processor, underwriter, and closer: thank you. This milestone belongs to all of you.',
    ],
  },
  {
    id: 'a2',
    category: 'Rate Desk',
    title: 'Q3 rate-lock policy update: what every loan officer needs to know',
    excerpt:
      'Effective Monday, lock extensions move to a 3-tier structure. Review the updated matrix before submitting new locks.',
    author: 'Secondary Marketing',
    authorAvatar: 'https://i.pravatar.cc/120?img=51',
    date: 'June 29, 2026',
    readTime: '3 min read',
    image:
      'https://images.unsplash.com/photo-1554224155-6726b3ff858f?q=80&w=2011&auto=format&fit=crop',
    body: [
      'Starting Monday, our lock-extension policy moves to a clearer, three-tier structure to better reflect market volatility.',
      'Tier 1 (1–7 days) carries a 0.125 cost, Tier 2 (8–15 days) a 0.25 cost, and Tier 3 (16–30 days) a 0.50 cost.',
      'Please review the full matrix in the Knowledge Library and reach out to the rate desk with any questions before submitting new locks.',
    ],
  },
  {
    id: 'a3',
    category: 'Growth',
    title: 'Independence opens a new branch in Austin, TX',
    excerpt:
      'Our 14th location is now welcoming borrowers in Central Texas. Meet the team leading the expansion.',
    author: 'People & Culture',
    authorAvatar: 'https://i.pravatar.cc/120?img=45',
    date: 'June 26, 2026',
    readTime: '2 min read',
    image:
      'https://images.unsplash.com/photo-1531218150217-54595bc2b934?q=80&w=2070&auto=format&fit=crop',
    body: [
      'We are thrilled to announce the opening of our newest branch in Austin, Texas — our 14th location nationwide.',
      'The Austin team brings deep local market expertise and a shared commitment to the Independence borrower experience.',
      'Stop by to say hello if you find yourself in Central Texas.',
    ],
  },
  {
    id: 'a4',
    category: 'People & Culture',
    title: '2026 benefits enrollment is now open',
    excerpt:
      'Review your medical, dental, vision, and 401(k) options. Enrollment closes June 30.',
    author: 'People & Culture',
    authorAvatar: 'https://i.pravatar.cc/120?img=45',
    date: 'June 24, 2026',
    readTime: '5 min read',
    image:
      'https://images.unsplash.com/photo-1521791136064-7986c2920216?q=80&w=2069&auto=format&fit=crop',
    body: [
      'Open enrollment for the 2026 plan year is here. This is your annual window to review and adjust your benefits.',
      'New this year: an expanded mental-health benefit and a higher 401(k) match of up to 5%.',
      'Enrollment closes June 30 — don\u2019t miss it.',
    ],
  },
  {
    id: 'a5',
    category: 'Operations',
    title: 'New Encompass workflow rollout begins next week',
    excerpt:
      'A streamlined milestone flow reduces clicks and speeds up handoffs between processing and underwriting.',
    author: 'Operations',
    authorAvatar: 'https://i.pravatar.cc/120?img=60',
    date: 'June 22, 2026',
    readTime: '3 min read',
    image:
      'https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=2070&auto=format&fit=crop',
    body: [
      'We are rolling out a redesigned Encompass workflow that cuts redundant data entry and clarifies handoffs.',
      'Training sessions are scheduled throughout the week — check the Events page to RSVP.',
    ],
  },
]

/* ------------------------------ Must reads ------------------------------- */

export interface MustRead {
  id: string
  title: string
  category: string
  due: string
  icon: any
}

export const mustReads: MustRead[] = [
  {
    id: 'm1',
    title: 'Updated TRID disclosure timeline',
    category: 'Compliance',
    due: 'Acknowledge by Fri',
    icon: FileText,
  },
  {
    id: 'm2',
    title: '2026 benefits enrollment is open',
    category: 'People & Culture',
    due: 'Closes Jun 30',
    icon: Award,
  },
  {
    id: 'm3',
    title: 'New Encompass workflow rollout',
    category: 'Operations',
    due: 'Read by Thu',
    icon: BookOpen,
  },
]

/* --------------------------------- Feed ---------------------------------- */

export interface Comment {
  id: string
  author: string
  avatar: string
  time: string
  body: string
}

export interface FeedPost {
  id: string
  author: string
  role: string
  avatar: string
  time: string
  team: string
  body: string
  image?: string
  likes: number
  comments: Comment[]
}

export const feedPosts: FeedPost[] = [
  {
    id: 'p1',
    author: 'Marcus Reyes',
    role: 'Branch Manager, Phoenix',
    avatar: 'https://i.pravatar.cc/120?img=12',
    time: '2h ago',
    team: 'Phoenix Branch',
    body: 'Huge shoutout to the Phoenix team for closing 38 loans this month — our best ever. Lunch is on me Friday! 🎉',
    image:
      'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?q=80&w=2070&auto=format&fit=crop',
    likes: 42,
    comments: [
      {
        id: 'c1',
        author: 'Priya Anand',
        avatar: 'https://i.pravatar.cc/80?img=5',
        time: '1h ago',
        body: 'Incredible work team! 👏',
      },
      {
        id: 'c2',
        author: 'Elena Cruz',
        avatar: 'https://i.pravatar.cc/80?img=47',
        time: '45m ago',
        body: 'So proud to be part of this branch.',
      },
    ],
  },
  {
    id: 'p2',
    author: 'Priya Anand',
    role: 'Senior Loan Officer',
    avatar: 'https://i.pravatar.cc/120?img=5',
    time: '5h ago',
    team: 'Sales',
    body: 'Reminder: the new pre-approval letter template is live in DocuSign. Please switch over by end of week — the old one will be retired.',
    likes: 17,
    comments: [
      {
        id: 'c3',
        author: 'Trent Baker',
        avatar: 'https://i.pravatar.cc/80?img=15',
        time: '3h ago',
        body: 'Thanks for the heads up!',
      },
    ],
  },
  {
    id: 'p3',
    author: 'Training & Enablement',
    role: 'Official channel',
    avatar: 'https://i.pravatar.cc/120?img=33',
    time: '1d ago',
    team: 'Learning',
    body: 'New on-demand course: "Explaining rate buydowns to first-time buyers." 20 minutes, counts toward your CE credits.',
    likes: 64,
    comments: [],
  },
]

/* --------------------------------- Apps ---------------------------------- */

export interface AppLink {
  label: string
  initials: string
  color: string
  category?: string
  description?: string
  url?: string
}

export const appLinks: AppLink[] = [
  {
    label: 'Velocify',
    initials: 'VE',
    color: 'bg-sky',
    category: 'Sales',
    description: 'Sales acceleration & lead management',
    url: 'https://lm.velocify.com/',
  },
  {
    label: 'Encompass',
    initials: 'EN',
    color: 'bg-gold-500',
    category: 'Operations',
    description: 'Loan origination system',
  },
  {
    label: 'Paylocity',
    initials: 'PA',
    color: 'bg-sage',
    category: 'HR',
    description: 'Payroll & benefits',
    url: 'https://access.paylocity.com',
  },
  {
    label: 'Optimal Blue',
    initials: 'OB',
    color: 'bg-navy-500',
    category: 'Operations',
    description: 'Pricing engine',
  },
  {
    label: 'Confluence',
    initials: 'CF',
    color: 'bg-sky',
    category: 'Secondary',
    description: 'Wiki & docs',
  },
  {
    label: 'JIRA',
    initials: 'JR',
    color: 'bg-sage',
    category: 'Technology',
    description: 'Issue tracking & IT requests',
  },
  {
    label: 'Surefire',
    initials: 'SU',
    color: 'bg-clay',
    category: 'Sales',
    description: 'CRM & marketing automation',
    url: 'https://sf3.tomnx.com/',
  },
  {
    label: 'UWM Investor Portal',
    initials: 'UW',
    color: 'bg-navy-600',
    category: 'Finance',
    description: 'Investor loan delivery & status',
  },
  {
    label: 'EPM Investor Portal',
    initials: 'EP',
    color: 'bg-periwinkle',
    category: 'Finance',
    description: 'Investor loan delivery & status',
  },
  {
    label: 'ComplianceEase',
    initials: 'CE',
    color: 'bg-sage',
    category: 'Compliance',
    description: 'Loan compliance & audit checks',
  },
  {
    label: 'ProctorNet',
    initials: 'PN',
    color: 'bg-gold-500',
    category: 'Compliance',
    description: 'Policy attestations & training records',
  },
]

/* -------------------------------- Events --------------------------------- */

export interface EventItem {
  id: string
  title: string
  day: string
  month: string
  time: string
  type: string
  location?: string
  description?: string
}

export const events: EventItem[] = [
  {
    id: 'e1',
    title: 'Q3 All-Hands Town Hall',
    day: '02',
    month: 'JUL',
    time: '10:00 AM CT',
    type: 'Company',
    location: 'Zoom + HQ Auditorium',
    description:
      'Quarterly business update from the leadership team, followed by live Q&A.',
  },
  {
    id: 'e2',
    title: 'Compliance Refresher: Fair Lending',
    day: '08',
    month: 'JUL',
    time: '1:00 PM CT',
    type: 'Training',
    location: 'Zoom',
    description:
      'Required annual fair-lending training for all customer-facing staff.',
  },
  {
    id: 'e3',
    title: 'Top Producer Awards Dinner',
    day: '15',
    month: 'JUL',
    time: '6:30 PM CT',
    type: 'Culture',
    location: 'The Grand Ballroom, Dallas',
    description: 'Celebrating our highest performers of the quarter.',
  },
  {
    id: 'e4',
    title: 'Encompass Workflow Training',
    day: '18',
    month: 'JUL',
    time: '11:00 AM CT',
    type: 'Training',
    location: 'Zoom',
    description: 'Hands-on walkthrough of the new milestone workflow.',
  },
  {
    id: 'e5',
    title: 'New Hire Welcome Coffee',
    day: '22',
    month: 'JUL',
    time: '9:00 AM CT',
    type: 'Culture',
    location: 'Each branch',
    description: 'Casual meet-and-greet for our newest team members.',
  },
]

/* -------------------------------- People --------------------------------- */

export interface Person {
  name: string
  detail: string
  avatar: string
}

export interface Employee {
  id: string
  name: string
  title: string
  department: string
  location: string
  email: string
  phone: string
  avatar: string
  manager?: string
}

export const employees: Employee[] = [
  {
    id: 'u1',
    name: 'Dana Whitfield',
    title: 'Chief Executive Officer',
    department: 'Executive',
    location: 'Dallas, TX',
    email: 'dana.whitfield@indephl.com',
    phone: '(214) 555-0101',
    avatar: 'https://i.pravatar.cc/200?img=32',
  },
  {
    id: 'u2',
    name: 'Marcus Reyes',
    title: 'Branch Manager',
    department: 'Sales',
    location: 'Phoenix, AZ',
    email: 'marcus.reyes@indephl.com',
    phone: '(602) 555-0114',
    avatar: 'https://i.pravatar.cc/200?img=12',
    manager: 'Dana Whitfield',
  },
  {
    id: 'u3',
    name: 'Priya Anand',
    title: 'Senior Loan Officer',
    department: 'Sales',
    location: 'Phoenix, AZ',
    email: 'priya.anand@indephl.com',
    phone: '(602) 555-0119',
    avatar: 'https://i.pravatar.cc/200?img=5',
    manager: 'Marcus Reyes',
  },
  {
    id: 'u4',
    name: 'Elena Cruz',
    title: 'Loan Processor',
    department: 'Operations',
    location: 'Remote',
    email: 'elena.cruz@indephl.com',
    phone: '(602) 555-0147',
    avatar: 'https://i.pravatar.cc/200?img=47',
    manager: 'Marcus Reyes',
  },
  {
    id: 'u5',
    name: 'James Okafor',
    title: 'Underwriter',
    department: 'Operations',
    location: 'Atlanta, GA',
    email: 'james.okafor@indephl.com',
    phone: '(404) 555-0168',
    avatar: 'https://i.pravatar.cc/200?img=68',
  },
  {
    id: 'u6',
    name: 'Sofia Lindqvist',
    title: 'Loan Officer',
    department: 'Sales',
    location: 'Denver, CO',
    email: 'sofia.lindqvist@indephl.com',
    phone: '(720) 555-0120',
    avatar: 'https://i.pravatar.cc/200?img=20',
    manager: 'Marcus Reyes',
  },
  {
    id: 'u7',
    name: 'Trent Baker',
    title: 'Jr. Underwriter',
    department: 'Operations',
    location: 'Remote',
    email: 'trent.baker@indephl.com',
    phone: '(720) 555-0115',
    avatar: 'https://i.pravatar.cc/200?img=15',
    manager: 'James Okafor',
  },
  {
    id: 'u8',
    name: 'Nina Petrova',
    title: 'VP, Secondary Marketing',
    department: 'Capital Markets',
    location: 'Dallas, TX',
    email: 'nina.petrova@indephl.com',
    phone: '(214) 555-0151',
    avatar: 'https://i.pravatar.cc/200?img=24',
    manager: 'Dana Whitfield',
  },
  {
    id: 'u9',
    name: 'Carlos Mendez',
    title: 'IT Support Lead',
    department: 'Technology',
    location: 'Dallas, TX',
    email: 'carlos.mendez@indephl.com',
    phone: '(214) 555-0190',
    avatar: 'https://i.pravatar.cc/200?img=53',
    manager: 'Dana Whitfield',
  },
  {
    id: 'u10',
    name: 'Aisha Rahman',
    title: 'People & Culture Manager',
    department: 'People & Culture',
    location: 'Dallas, TX',
    email: 'aisha.rahman@indephl.com',
    phone: '(214) 555-0145',
    avatar: 'https://i.pravatar.cc/200?img=45',
    manager: 'Dana Whitfield',
  },
  {
    id: 'u11',
    name: 'Ben Carter',
    title: 'Compliance Officer',
    department: 'Compliance',
    location: 'Remote',
    email: 'ben.carter@indephl.com',
    phone: '(800) 555-0177',
    avatar: 'https://i.pravatar.cc/200?img=51',
  },
  {
    id: 'u12',
    name: 'Grace Liu',
    title: 'Marketing Director',
    department: 'Marketing',
    location: 'Austin, TX',
    email: 'grace.liu@indephl.com',
    phone: '(512) 555-0133',
    avatar: 'https://i.pravatar.cc/200?img=44',
    manager: 'Dana Whitfield',
  },
]

export const birthdays: Person[] = [
  {
    name: 'Elena Cruz',
    detail: 'Processing • Today',
    avatar: 'https://i.pravatar.cc/80?img=47',
  },
  {
    name: 'James Okafor',
    detail: 'Underwriting • Tomorrow',
    avatar: 'https://i.pravatar.cc/80?img=68',
  },
]

export const newHires: Person[] = [
  {
    name: 'Sofia Lindqvist',
    detail: 'Loan Officer • Denver',
    avatar: 'https://i.pravatar.cc/80?img=20',
  },
  {
    name: 'Trent Baker',
    detail: 'Jr. Underwriter • Remote',
    avatar: 'https://i.pravatar.cc/80?img=15',
  },
]

/* ----------------------------- Recognition ------------------------------- */

export interface Recognition {
  id: string
  from: string
  fromAvatar: string
  to: string
  toAvatar: string
  value: string
  message: string
  likes: number
  time: string
}

export const recognitionFeed: Recognition[] = [
  {
    id: 'r1',
    from: 'Dana Whitfield',
    fromAvatar: 'https://i.pravatar.cc/80?img=32',
    to: 'Priya Anand',
    toAvatar: 'https://i.pravatar.cc/80?img=5',
    value: 'Borrower First',
    message:
      'went above and beyond to save a tough VA file at the last minute. A true example of putting borrowers first.',
    likes: 28,
    time: '2h ago',
  },
  {
    id: 'r2',
    from: 'Marcus Reyes',
    fromAvatar: 'https://i.pravatar.cc/80?img=12',
    to: 'The Phoenix Ops Team',
    toAvatar: 'https://i.pravatar.cc/80?img=47',
    value: 'One Team',
    message: 'for flawless month-end funding. Incredible work under pressure.',
    likes: 41,
    time: '6h ago',
  },
  {
    id: 'r3',
    from: 'Aisha Rahman',
    fromAvatar: 'https://i.pravatar.cc/80?img=45',
    to: 'Carlos Mendez',
    toAvatar: 'https://i.pravatar.cc/80?img=53',
    value: 'Own It',
    message:
      'jumped on a critical system outage at 6am and had us back online before the first lock of the day.',
    likes: 19,
    time: '1d ago',
  },
]

export const kudosValues = [
  'Borrower First',
  'One Team',
  'Own It',
  'Raise the Bar',
  'Do the Right Thing',
]

export interface LeaderboardEntry {
  id: string
  name: string
  avatar: string
  department: string
  kudos: number
}

export const leaderboard: LeaderboardEntry[] = [
  {
    id: 'u3',
    name: 'Priya Anand',
    avatar: 'https://i.pravatar.cc/80?img=5',
    department: 'Sales',
    kudos: 47,
  },
  {
    id: 'u4',
    name: 'Elena Cruz',
    avatar: 'https://i.pravatar.cc/80?img=47',
    department: 'Operations',
    kudos: 39,
  },
  {
    id: 'u9',
    name: 'Carlos Mendez',
    avatar: 'https://i.pravatar.cc/80?img=53',
    department: 'Technology',
    kudos: 31,
  },
  {
    id: 'u2',
    name: 'Marcus Reyes',
    avatar: 'https://i.pravatar.cc/80?img=12',
    department: 'Sales',
    kudos: 28,
  },
  {
    id: 'u5',
    name: 'James Okafor',
    avatar: 'https://i.pravatar.cc/80?img=68',
    department: 'Operations',
    kudos: 22,
  },
]

/* ------------------------------- Documents ------------------------------- */

export interface DocItem {
  id: string
  title: string
  category: string
  type: 'PDF' | 'DOCX' | 'XLSX' | 'Policy' | 'Guide'
  updated: string
  size: string
  owner: string
}

export const documents: DocItem[] = [
  {
    id: 'd1',
    title: 'Employee Handbook 2026',
    category: 'People & Culture',
    type: 'PDF',
    updated: 'Jun 1, 2026',
    size: '2.4 MB',
    owner: 'People & Culture',
  },
  {
    id: 'd2',
    title: 'TRID Disclosure Timeline',
    category: 'Compliance',
    type: 'Policy',
    updated: 'Jun 28, 2026',
    size: '480 KB',
    owner: 'Compliance',
  },
  {
    id: 'd3',
    title: 'Rate-Lock Extension Matrix',
    category: 'Rate Desk',
    type: 'XLSX',
    updated: 'Jun 29, 2026',
    size: '120 KB',
    owner: 'Secondary Marketing',
  },
  {
    id: 'd4',
    title: 'Pre-Approval Letter Template',
    category: 'Sales',
    type: 'DOCX',
    updated: 'Jun 20, 2026',
    size: '64 KB',
    owner: 'Sales Enablement',
  },
  {
    id: 'd5',
    title: 'Encompass Workflow Guide',
    category: 'Operations',
    type: 'Guide',
    updated: 'Jun 22, 2026',
    size: '1.1 MB',
    owner: 'Operations',
  },
  {
    id: 'd6',
    title: 'Fair Lending Policy',
    category: 'Compliance',
    type: 'Policy',
    updated: 'May 30, 2026',
    size: '720 KB',
    owner: 'Compliance',
  },
  {
    id: 'd7',
    title: 'Benefits Summary 2026',
    category: 'People & Culture',
    type: 'PDF',
    updated: 'Jun 24, 2026',
    size: '900 KB',
    owner: 'People & Culture',
  },
  {
    id: 'd8',
    title: 'Brand Style Guide',
    category: 'Marketing',
    type: 'PDF',
    updated: 'Apr 12, 2026',
    size: '5.2 MB',
    owner: 'Marketing',
  },
]

export const knowledgeArticles: DocItem[] = [
  {
    id: 'k1',
    title: 'How to submit a new rate lock',
    category: 'Lending',
    type: 'Guide',
    updated: 'Jun 29, 2026',
    size: '—',
    owner: 'Rate Desk',
  },
  {
    id: 'k2',
    title: 'VA loan eligibility checklist',
    category: 'Lending',
    type: 'Guide',
    updated: 'Jun 18, 2026',
    size: '—',
    owner: 'Underwriting',
  },
  {
    id: 'k3',
    title: 'Explaining rate buydowns to first-time buyers',
    category: 'Sales',
    type: 'Guide',
    updated: 'Jun 15, 2026',
    size: '—',
    owner: 'Training',
  },
  {
    id: 'k4',
    title: 'Requesting time off in Workday',
    category: 'People & Culture',
    type: 'Guide',
    updated: 'Jun 10, 2026',
    size: '—',
    owner: 'People & Culture',
  },
  {
    id: 'k5',
    title: 'Setting up MFA for company apps',
    category: 'IT',
    type: 'Guide',
    updated: 'Jun 5, 2026',
    size: '—',
    owner: 'Technology',
  },
  {
    id: 'k6',
    title: 'Loan file naming conventions',
    category: 'Operations',
    type: 'Guide',
    updated: 'May 28, 2026',
    size: '—',
    owner: 'Operations',
  },
]

/* ------------------------------ Teams / Sites ---------------------------- */

export interface TeamSite {
  id: string
  name: string
  members: number
  description: string
  color: string
}

export const teamSites: TeamSite[] = [
  {
    id: 't1',
    name: 'Ignite Sales',
    members: 45,
    description:
      'Sales strategies, updates, and discussion for the Ignite team.',
    color: 'bg-navy-600',
  },
  {
    id: 't2',
    name: 'Scottsdale Office',
    members: 32,
    description: 'News, resources, and discussion for the Scottsdale branch.',
    color: 'bg-gold-500',
  },
  {
    id: 't3',
    name: 'Miami Office',
    members: 28,
    description: 'News, resources, and discussion for the Miami branch.',
    color: 'bg-sage',
  },
  {
    id: 't4',
    name: 'Southfield Office',
    members: 35,
    description: 'News, resources, and discussion for the Southfield branch.',
    color: 'bg-periwinkle',
  },
  {
    id: 't12',
    name: 'Maryland Office',
    members: 26,
    description: 'News, resources, and discussion for the Maryland branch.',
    color: 'bg-sky',
  },
  {
    id: 't13',
    name: 'Secondary',
    members: 10,
    description: 'Pricing, rate locks, and secondary market activity.',
    color: 'bg-clay',
  },
  {
    id: 't5',
    name: 'Operations',
    members: 82,
    description: 'Processing, underwriting, and closing workflows.',
    color: 'bg-clay',
  },
  {
    id: 't6',
    name: 'Compliance',
    members: 14,
    description: 'Regulatory updates and required training.',
    color: 'bg-sky',
  },
  {
    id: 't7',
    name: 'Closing',
    members: 22,
    description: 'Finalizing loans and ensuring smooth closings.',
    color: 'bg-navy-500',
  },
  {
    id: 't8',
    name: 'Finance',
    members: 18,
    description: 'Accounting, payroll, and financial reporting.',
    color: 'bg-gold-400',
  },
  {
    id: 't9',
    name: 'Technology & Innovation',
    members: 25,
    description: 'IT support, app access, and new tech initiatives.',
    color: 'bg-sage',
  },
  {
    id: 't10',
    name: 'Human Resources',
    members: 12,
    description: 'Benefits, employee relations, and HR policies.',
    color: 'bg-periwinkle',
  },
  {
    id: 't11',
    name: 'Recruiting',
    members: 8,
    description: 'Hiring updates, open roles, and talent acquisition.',
    color: 'bg-clay',
  },
  {
    id: 't14',
    name: 'Marketing',
    members: 16,
    description: 'Brand, campaigns, content, and marketing resources.',
    color: 'bg-gold-500',
  },
]

/* ----------------------------- Notifications ----------------------------- */

export interface NotificationItem {
  id: string
  avatar: string
  text: string
  time: string
  to: string
  unread: boolean
}

export const notifications: NotificationItem[] = [
  {
    id: 'n1',
    avatar: 'https://i.pravatar.cc/80?img=5',
    text: 'Priya Anand commented on the Phoenix branch post.',
    time: '12m ago',
    to: '/feed/p1',
    unread: true,
  },
  {
    id: 'n2',
    avatar: 'https://i.pravatar.cc/80?img=32',
    text: 'Dana Whitfield recognized you for Borrower First. 🎉',
    time: '1h ago',
    to: '/recognition',
    unread: true,
  },
  {
    id: 'n3',
    avatar: 'https://i.pravatar.cc/80?img=51',
    text: 'New must-read: Q3 rate-lock policy update.',
    time: '3h ago',
    to: '/news/a2',
    unread: true,
  },
  {
    id: 'n4',
    avatar: 'https://i.pravatar.cc/80?img=47',
    text: 'Elena Cruz has a birthday today. Send a note!',
    time: '5h ago',
    to: '/people/u4',
    unread: false,
  },
  {
    id: 'n5',
    avatar: 'https://i.pravatar.cc/80?img=33',
    text: 'New CE course is available in Knowledge.',
    time: '1d ago',
    to: '/knowledge',
    unread: false,
  },
]

/* ------------------------------ Loan pipeline ---------------------------- */

export interface PipelineStage {
  label: string
  count: number
  volume: string
  color: string
}

export const pipelineStages: PipelineStage[] = [
  {
    label: 'Applications',
    count: 42,
    volume: '$14.8M',
    color: 'bg-periwinkle',
  },
  { label: 'Processing', count: 28, volume: '$9.6M', color: 'bg-sky' },
  { label: 'Underwriting', count: 19, volume: '$6.7M', color: 'bg-gold-500' },
  { label: 'Clear to Close', count: 11, volume: '$4.1M', color: 'bg-sage' },
  { label: 'Funded (MTD)', count: 34, volume: '$12.2M', color: 'bg-navy-600' },
]

export interface RateRow {
  product: string
  rate: string
  apr: string
  change: number
}

export const rateWatch: RateRow[] = [
  { product: '30-Yr Fixed', rate: '6.625%', apr: '6.78%', change: -3 },
  { product: '15-Yr Fixed', rate: '5.875%', apr: '6.02%', change: -2 },
  { product: 'FHA 30-Yr', rate: '6.250%', apr: '6.41%', change: 0 },
  { product: 'VA 30-Yr', rate: '6.125%', apr: '6.29%', change: 1 },
  { product: '7/6 ARM', rate: '6.000%', apr: '6.55%', change: 4 },
]

export const rateUpdatedAt = 'as of 9:30 AM CT'
